import { getResultFromResponse, loop, retryWithFibonacci } from '@web3/utils';
import { useMemo } from 'react';
import { getUtcTime } from '../api';
import { EventBasedStore, pickStoreActions } from '../utils/EventBasedStore';

export type ServerUtcState = {
  loading: boolean;
  // 服务器时间和本地时间的差值
  serverLocalTimeDiff: number;
  //   本地时间
  localTime: number;
};

type ComputedState = {
  serverTime: number;
};

class ServerUtcTimeStore extends EventBasedStore<ServerUtcState, ComputedState> {
  constructor() {
    super(
      {
        loading: false,
        serverLocalTimeDiff: 0,
        localTime: Date.now(),
      },
      (state) => {
        return {
          serverTime: state.localTime + state.serverLocalTimeDiff,
        };
      },
      // 初始化函数
      () => {
        this.startUpdateServerDiff();
        this.startUpdateLocalTime();
      },
    );
  }

  //   开始同步服务器的时间diff到store中
  private _stopUpdateServer?: () => void;
  startUpdateServerDiff(interval = 70e3): void {
    this.stopUpdateServerDiff();
    this._stopUpdateServer = loop(
      () => {
        this.updateServerTime();
      },
      {
        interval,
        immediate: true,
      },
    );
  }

  stopUpdateServerDiff(): void {
    if (this._stopUpdateServer) {
      this._stopUpdateServer();
      this._stopUpdateServer = undefined;
    }
  }

  // 开始自动更新用户时间到store中
  private _stopUpdateLocal?: () => void;
  startUpdateLocalTime(interval = 1e3): void {
    this.stopUpdateLocalTime();
    this._stopUpdateLocal = loop(
      () => {
        this.setState({
          localTime: Date.now(),
        });
      },
      {
        interval,
        immediate: true,
      },
    );
  }

  stopUpdateLocalTime(): void {
    if (this._stopUpdateLocal) {
      this._stopUpdateLocal();
      this._stopUpdateLocal = undefined;
    }
  }

  async updateServerTime() {
    if (this.state.loading) {
      return;
    }
    this.setState({
      loading: true,
    });
    const diff = await retryWithFibonacci(async () => {
      const res = await getResultFromResponse(getUtcTime());
      const local = Date.now();
      return res.currentTimeMillis - local;
    }, 3).catch((e) => {
      console.error(e);
      //   服务器时间获取失败的情况，就用只能用本地时间了（diff 0）
      return 0;
    });
    this.setState({
      loading: false,
      serverLocalTimeDiff: diff,
    });
  }
}

export const serverUtcTimeStore = new ServerUtcTimeStore();

export const useServerUtcTime = () => {
  const state = serverUtcTimeStore.useStoreState();
  const actions = useMemo(
    () =>
      pickStoreActions(serverUtcTimeStore, [
        'startUpdateServerDiff',
        'stopUpdateServerDiff',
        'startUpdateLocalTime',
        'stopUpdateLocalTime',
        'updateServerTime',
      ]),
    [],
  );

  return {
    ...state,
    ...actions,
  };
};
